import { Component, Input, Output, EventEmitter, ContentChildren, QueryList } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as XLSX from 'xlsx';
import { TableColumnComponent } from './table-column.component';
import { EmptyStateComponent } from '../empty-state/empty-state.component';

@Component({
  standalone: true,
  selector: 'app-data-table',
  imports: [CommonModule, FormsModule, EmptyStateComponent],
  template: `
    <div class="dt-toolbar" *ngIf="searchable || exportable || title">
      <span class="dt-title" *ngIf="title">{{ title }}</span>
      <div class="dt-search" *ngIf="searchable">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
        <input type="text" [(ngModel)]="search" [placeholder]="searchPlaceholder">
      </div>
      <button class="dt-export" *ngIf="exportable" (click)="exportExcel()" [disabled]="!filtered.length" title="Export to Excel">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 3v12m0 0 4-4m-4 4-4-4"/><path d="M5 21h14"/></svg>
        Export
      </button>
    </div>

    <div class="dt-wrap">
      <table class="dt" *ngIf="!loading && filtered.length">
        <thead>
          <tr>
            <th *ngFor="let c of columns" [style.width]="c.width"
                [class.dt-center]="c.align === 'center'" [class.dt-right]="c.align === 'right'">{{ c.header }}</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of filtered; trackBy: trackRow" [class.dt-clickable]="rowClickable"
              (click)="rowClickable && rowClick.emit(row)">
            <td *ngFor="let c of columns"
                [class.dt-center]="c.align === 'center'" [class.dt-right]="c.align === 'right'">
              <ng-container *ngTemplateOutlet="c.cell || null; context: { $implicit: row }"></ng-container>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="dt-loading" *ngIf="loading"><span class="dt-spinner"></span></div>

      <app-empty-state *ngIf="!loading && !filtered.length"
        [icon]="emptyIcon"
        [heading]="search ? 'No results' : emptyTitle"
        [message]="search ? 'No rows match your search' : emptyMessage">
        <ng-content select="[emptyAction]" *ngIf="!search"></ng-content>
      </app-empty-state>
    </div>
  `,
  styles: [`
    .dt-toolbar { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
    .dt-title { font-size: 15px; font-weight: 700; color: #2a2024; margin-right: auto; }
    .dt-search { flex: 1; max-width: 360px; display: flex; align-items: center; gap: 8px;
      border: 1px solid #e8e0dc; border-radius: 10px; padding: 0 12px; background: #fff; }
    .dt-search svg { width: 16px; height: 16px; color: #9a8f93; flex-shrink: 0; }
    .dt-search input { border: 0; outline: 0; padding: 10px 0; font-size: 13px; width: 100%; background: transparent; color: #2a2024; }
    .dt-export { display: inline-flex; align-items: center; gap: 6px; height: 38px; padding: 0 14px;
      border: 1px solid #e8e0dc; border-radius: 10px; background: #fff; color: #334155; font-size: 13px;
      font-weight: 600; cursor: pointer; transition: background .15s, border-color .15s; }
    .dt-export svg { width: 15px; height: 15px; }
    .dt-export:hover:not(:disabled) { background: rgba(51,65,85,.05); border-color: #334155; }
    .dt-export:disabled { opacity: .5; cursor: not-allowed; }

    .dt-wrap { width: 100%; overflow-x: auto; }
    .dt { width: 100%; border-collapse: collapse; font-size: 13px; font-family: 'Inter', sans-serif; }
    .dt thead th {
      text-align: left; padding: 12px 18px; font-size: 11px; font-weight: 700; letter-spacing: .5px;
      text-transform: uppercase; color: #9a8f93; background: #faf7f3; border-bottom: 1.5px solid #ede6dc; white-space: nowrap;
    }
    .dt tbody td { padding: 13px 18px; border-bottom: 1px solid #f1ebe9; color: #2a2024; vertical-align: middle; }
    .dt tbody tr { transition: background .12s; }
    .dt tbody tr:hover { background: #fcf7f7; }
    .dt tbody tr:last-child td { border-bottom: 0; }
    .dt-clickable { cursor: pointer; }
    .dt-center { text-align: center !important; }
    .dt-right { text-align: right !important; }
    .dt-loading { display: flex; justify-content: center; padding: 60px; }
    .dt-spinner { width: 30px; height: 30px; border: 3px solid rgba(51,65,85,.12);
      border-top-color: #334155; border-radius: 50%; animation: dtspin .7s linear infinite; }
    @keyframes dtspin { to { transform: rotate(360deg); } }
  `],
})
export class DataTableComponent {
  @Input() data: any[] = [];
  @Input() loading = false;
  @Input() trackByKey = 'id';
  @Input() rowClickable = false;
  @Output() rowClick = new EventEmitter<any>();

  @Input() searchable = false;
  @Input() searchPlaceholder = 'Search…';
  @Input() exportable = false;
  @Input() exportName = 'export';
  @Input() title = '';

  @Input() emptyIcon = 'inbox';
  @Input() emptyTitle = 'Nothing here yet';
  @Input() emptyMessage = '';

  @ContentChildren(TableColumnComponent) columns!: QueryList<TableColumnComponent>;

  search = '';
  trackRow = (i: number, row: any) => row?.[this.trackByKey] ?? i;

  get filtered(): any[] {
    const term = (this.search || '').trim().toLowerCase();
    if (!term) return this.data || [];
    const cols = this.columns?.toArray() || [];
    return (this.data || []).filter(row =>
      cols.some(c => String(c.read(row) ?? '').toLowerCase().includes(term)));
  }

  exportExcel(): void {
    const cols = (this.columns?.toArray() || []).filter(c => c.exportable && (c.field || c.value));
    const header = cols.map(c => c.header);
    const aoa: any[][] = [header, ...this.filtered.map(row => cols.map(c => c.read(row) ?? ''))];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, `${this.exportName}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }
}
